import React, { useState, useEffect } from "react"
import {
  Form,
  Input,
  Button,
  Checkbox,
  message as antdMessage,
  Spin,
} from "antd"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import usePasswordValidation from "../../utils/PasswordCheck"
import { authApi } from "../../Apis/authApis"
import { setUserCookies } from "../../utils/Cookies"
import { fetchUserDetails } from "../../ReduxToolKit/Slice/UserSlice"
import { translations } from "../../utils/Translation/staticTranslation"
import ToggleLogin from "../../components/Common/ToggleLogin"
import PrivacyPolicyModal from "../../components/Common/Modals/PrivacyPolicyModal"
import TermsOfServiceModal from "../../components/Common/Modals/TermsOfServiceModal"
import useLanguage from "../../services/useLanguage"
import { getMarketingWebsiteUrl } from "../../utils/getMarketingWebsiteUrl"

/**
 * Invite completion for admin-invited users (no store/business fields).
 * Route: /invite-signup-users — same query params as invite-signup except business names.
 */
const InviteSignupUsers = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { language, handleLanguageChange } = useLanguage()

  const email = searchParams.get("email")
  const role = searchParams.get("role")
  const token = searchParams.get("token")
  const [loading, setLoading] = useState(false)
  const [tokenValid, setTokenValid] = useState(true)
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false)
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)

  const { passwordChecked, validatePassword } = usePasswordValidation()
  const lang = localStorage.getItem("language") || "ar"
  const signup = translations[lang]
  const { common_error_msg } = translations[lang]
  const { AnalyticsPages } = translations[lang]
  const signupPage = translations[lang]?.signup
  const password = translations[lang]?.reset_password
  const loginPage = translations[lang]?.loginPage
  const websiteUrl = getMarketingWebsiteUrl(lang)

  useEffect(() => {
    const validateSignupToken = async () => {
      if (token) {
        try {
          setLoading(true)
          const res = await authApi.validateToken(token, role)
          if (res?.status === 200) {
            setTokenValid(true)
          } else {
            setTokenValid(false)
          }
        } catch (err) {
          console.error("Token validation failed:", err)
          setTokenValid(false)
        } finally {
          setLoading(false)
        }
      }
    }

    validateSignupToken()
  }, [token, role])

  const onFinish = async (values) => {
    if (!passwordChecked.lengthCheck || !passwordChecked.otherChecks) {
      return antdMessage.error(common_error_msg?.password_not_meet_requirements)
    }

    if (values.password !== values.confirm_password) {
      return antdMessage.error(password?.passwordMatch)
    }

    setLoading(true)
    try {
      const payload = {
        vendor_name_english: values.vendor_name_english,
        vendor_name_arabic: values.vendor_name_arabic,
        email: values.email,
        password: values.password,
        confirm_password: values.confirm_password,
        token: token,
        role: role,
        phone: values.phone,
      }
      const response = await authApi.inviteSignUp(payload)
      if (response.status === 201) {
        const loginUser = { token: response.data.token }
        setUserCookies(loginUser)
        await dispatch(fetchUserDetails()).unwrap()
        antdMessage.success(AnalyticsPages?.welcome)
        navigate("/dashboard")
        form.resetFields()
      }
    } catch (error) {
      const message =
        lang === "en"
          ? error.response?.data?.error
          : error.response?.data?.errorArabic

      antdMessage.error(message)
      console.log("Error:::", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase()
    form.setFieldsValue({ email: value })
  }

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "")
    form.setFieldsValue({ phone: value })
  }

  if (!tokenValid) {
    return (
      <div className="login-bg-img">
        <div className="login-container">
          <div className="signup-inner invite-signup-page">
            <div className="signup-left">
              <div className="signup-left-logo">
                <a href={websiteUrl}>
                  <img src="/signup/Zory brown.svg" alt="Zory Logo" />
                </a>
              </div>
            </div>
            <div className="signup-right">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  textAlign: "center",
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#343c3e",
                }}
              >
                {signup?.signup?.invalid_link}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="signup-inner invite-signup-page">
        <div className="signup-left">
          <div className="signup-left-logo">
            <a href={websiteUrl}>
              <img src="/signup/Zory brown.svg" alt="Zory Logo" />
            </a>
          </div>
        </div>

        <div className="signup-right">
          <div className="sign-l-header" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="back-arrow-inv"></div>
            <div className="logo-sign">
              <a href={websiteUrl}>
                <img src="/signup/Zorysmall.svg" alt="Zory Logo" />
              </a>
            </div>
            <div className="lang-toggle">
              <ToggleLogin
                toggleLanguage={handleLanguageChange}
                currentLanguage={language}
              />
            </div>
          </div>

          <div className="content">
            <h3 className="font-Montserrat00">
              {lang === "ar" ? "إكمال التسجيل" : "Complete Registration"}
            </h3>
            <span className="font-Montserrat-300">
              {lang === "ar"
                ? "يرجى إكمال بياناتك لإنهاء عملية التسجيل"
                : "Please complete your details to finish registration"}
            </span>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ marginTop: "24px" }}
            initialValues={{
              email,
              agreeToTerms: false,
            }}
          >
            <div style={{ display: "flex", gap: "16px" }}>
              <Form.Item
                label={<span style={{ fontSize: "13px" }}>Full Name</span>}
                name="vendor_name_english"
                rules={[
                  {
                    required: true,
                    message: "Full name is required",
                  },
                ]}
                labelCol={{ span: 24 }}
                style={{ flex: 1 }}
              >
                <Input
                  className="custom-input"
                  placeholder="Enter full name"
                  style={{
                    background: "#ffffff",
                    border: "1px solid rgba(132, 135, 136, 0.25)",
                    height: 50,
                    borderRadius: 12,
                    direction: "ltr",
                    textAlign: "left",
                  }}
                />
              </Form.Item>

              <Form.Item
                label={<label className="ar-label">الاسم الكامل</label>}
                name="vendor_name_arabic"
                labelCol={{ span: 24 }}
                style={{
                  flex: 1,
                  direction: "rtl",
                  textAlign: "right",
                }}
              >
                <Input
                  className="custom-input"
                  placeholder="الاسم الكامل"
                  style={{
                    background: "#ffffff",
                    border: "1px solid rgba(132, 135, 136, 0.25)",
                    height: 50,
                    borderRadius: 12,
                    direction: "rtl",
                    textAlign: "right",
                  }}
                />
              </Form.Item>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <Form.Item
                label={
                  <span className={lang === "ar" ? "ar-label" : ""}>
                    {lang === "ar" ? "بريد إلكتروني" : "Email"}
                  </span>
                }
                name="email"
                rules={[
                  {
                    required: true,
                    message: loginPage?.email_error_1,
                  },
                  {
                    type: "email",
                    message: loginPage?.email_error_2,
                  },
                ]}
                labelCol={{ span: 24 }}
                style={{ flex: 1 }}
              >
                <Input
                  className="custom-input-disabled"
                  placeholder={loginPage?.email_placeholder}
                  onChange={handleEmailChange}
                  disabled
                  style={{
                    background: "#f5f5f5",
                    border: "1px solid rgba(132, 135, 136, 0.25)",
                    height: 50,
                    borderRadius: 12,
                    color: "#8c8c8c",
                  }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className={lang === "ar" ? "ar-label" : ""}>
                    {lang === "ar" ? "رقم الهاتف" : "Phone Number"}
                  </span>
                }
                name="phone"
                rules={[
                  {
                    required: true,
                    message:
                      lang === "ar"
                        ? "رقم الهاتف مطلوب"
                        : "Phone number is required",
                  },
                  {
                    min: 8,
                    message:
                      lang === "ar"
                        ? "رقم الهاتف يجب أن يكون 8 أرقام على الأقل"
                        : "Phone number must be at least 8 digits",
                  },
                ]}
                labelCol={{ span: 24 }}
                style={{ flex: 1 }}
              >
                <Input
                  className="custom-input"
                  placeholder={
                    lang === "ar" ? "أدخل رقم الهاتف" : "Enter phone number"
                  }
                  onChange={handlePhoneChange}
                  style={{
                    background: "#ffffff",
                    border: "1px solid rgba(132, 135, 136, 0.25)",
                    height: 50,
                    borderRadius: 12,
                  }}
                />
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className={lang === "ar" ? "ar-label" : ""}>
                  {lang === "ar" ? "كلمة المرور" : "Password"}
                </span>
              }
              name="password"
              rules={[
                {
                  required: true,
                  message: password?.password_error,
                },
                {
                  min: 8,
                  message: password?.validation_2,
                },
              ]}
              labelCol={{ span: 24 }}
            >
              <Input.Password
                className="custom-input"
                placeholder={signupPage?.passwordPlaceholder}
                onChange={(e) => validatePassword(e.target.value)}
                style={{
                  background: "#ffffff",
                  border: "1px solid rgba(132, 135, 136, 0.25)",
                  height: 50,
                  borderRadius: 12,
                }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span className={lang === "ar" ? "ar-label" : ""}>
                  {password?.confirm_password}
                </span>
              }
              name="confirm_password"
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: password?.confirm_password_error,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(password?.passwordMatch || "")
                    )
                  },
                }),
              ]}
              labelCol={{ span: 24 }}
            >
              <Input.Password
                className="custom-input"
                placeholder={password?.confirm_password}
                style={{
                  background: "#ffffff",
                  border: "1px solid rgba(132, 135, 136, 0.25)",
                  height: 50,
                  borderRadius: 12,
                }}
              />
            </Form.Item>

            <div style={{ marginBottom: "16px", marginTop: "-8px" }}>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  fontSize: "14px",
                }}
              >
                <li
                  style={{
                    color: passwordChecked.otherChecks ? "#039855" : "#3F3F3F",
                    marginBottom: "4px",
                  }}
                >
                  • {password?.validation_1}
                </li>
                <li
                  style={{
                    color: passwordChecked.lengthCheck ? "#039855" : "#3F3F3F",
                  }}
                >
                  • {password?.validation_2}
                </li>
              </ul>
            </div>

            <Form.Item
              name="agreeToTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(loginPage?.agree_error),
                },
              ]}
              className="login-terms-form-item"
            >
              <Checkbox className="login-terms-checkbox">
                <span className="login-terms-text">
                  {loginPage?.agree_prefix}{" "}
                  <button
                    type="button"
                    className="login-terms-link-btn"
                    onClick={(e) => {
                      e.preventDefault()
                      setIsTermsModalOpen(true)
                    }}
                  >
                    {loginPage?.terms_of_service}
                  </button>{" "}
                  {loginPage?.and}{" "}
                  <button
                    type="button"
                    className="login-terms-link-btn"
                    onClick={(e) => {
                      e.preventDefault()
                      setIsPrivacyModalOpen(true)
                    }}
                  >
                    {loginPage?.privacy_policy_link}
                  </button>
                </span>
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
                style={{
                  width: "100%",
                  fontSize: "18px",
                  height: 50,
                  borderRadius: 12,
                  background: "#1d869a",
                  border: "none",
                }}
              >
                {loading ? <Spin /> : signupPage?.buttonText}
              </Button>
            </Form.Item>

            <div
              style={{
                textAlign: "center",
                fontSize: "14px",
                color: "#343c3e",
                marginTop: "8px",
              }}
            >
              {lang === "ar"
                ? "هل لديك حساب بالفعل؟"
                : "Already have an account?"}
              <a
                href="/login"
                style={{
                  color: "#1d869a",
                  fontWeight: "600",
                  textDecoration: "underline",
                  paddingLeft: lang === "ar" ? "" : "5px",
                  paddingRight: lang === "ar" ? "5px" : "",
                }}
              >
                {signupPage?.login ?? "Login"}
              </a>
            </div>
          </Form>
        </div>
      </div>

      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        translations={translations}
        currentLang={lang}
      />

      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        translations={translations}
        currentLang={lang}
      />
    </>
  )
}

export default InviteSignupUsers
